import type { PriceDaily, RMR } from "epsm-api-types/models";
import { ONE_MIN_IN_MS } from "@/lib/utils";
import {
	type Sectors,
	type SectorsChartType,
	SectorsTimeRange,
	type TimeRangeInfo,
} from "./types";

export const priceResponseFields: (keyof PriceDaily)[] = [
	"date",
	"adj_close",
	"ticker",
] as const;

export const rmrResponseFields: (keyof RMR)[] = [
	"ticker",
	"date",
	"index_ratio",
] as const;

export const priceResponseFieldsIntraday: (keyof PriceDaily)[] = [
	"date",
	"close",
	"ticker",
] as const;

export const SECTORS: Sectors = {
	XLB: { ticker: "XLB", name: "Materials", color: "#ff7f10" },
	XLC: { ticker: "XLC", name: "Comm Serv", color: "#03fcfc" },
	XLE: { ticker: "XLE", name: "Energy", color: "#8c564b" },
	XLF: { ticker: "XLF", name: "Financials", color: "#d62728" },
	XLI: { ticker: "XLI", name: "Industrials", color: "#e377c2" },
	XLK: { ticker: "XLK", name: "Technology", color: "#bcbd22" },
	XLP: { ticker: "XLP", name: "Cons Stapl", color: "#2ca02c" },
	XLRE: { ticker: "XLRE", name: "Real Estate", color: "#3d66cf" },
	XLU: { ticker: "XLU", name: "Utilities", color: "#7f7f7f" },
	XLV: { ticker: "XLV", name: "Health", color: "#845da8" },
	XLY: { ticker: "XLY", name: "Cons Disc", color: "#1f77b4" },
};

export const DEFAULT_COLOR = "#000000";

export const chartTypes = [
	"Price",
	"Ratio",
] as const satisfies readonly SectorsChartType[];

export const timeRanges = [
	SectorsTimeRange.OneYear,
	SectorsTimeRange.SixtyDays,
	SectorsTimeRange.FifteenDays,
] as const satisfies readonly SectorsTimeRange[];

export const triggerClassName =
	"w-[60px] news-select-trigger text-gold font-bold text-base p-1 h-full border-none cursor-pointer";

export const TIME_RANGE_MAP: Record<SectorsTimeRange, TimeRangeInfo> = {
	[SectorsTimeRange.OneYear]: {
		fields: priceResponseFields,
		rmrFields: rmrResponseFields,
		rmrClosePrice: "index_ratio",
		requestPath: "daily",
		isIntraday: false,
		duration: 365 * 24 * 60 * ONE_MIN_IN_MS,
		closePrice: "adj_close",
		xAxisStep: 25,
		dataFormat: "%m/%d/%y",
		tooltipWidth: 90,
	},
	[SectorsTimeRange.SixtyDays]: {
		fields: priceResponseFields,
		rmrFields: rmrResponseFields,
		rmrClosePrice: "index_ratio",
		requestPath: "daily",
		isIntraday: false,
		duration: 60 * 24 * 60 * ONE_MIN_IN_MS,
		closePrice: "adj_close",
		xAxisStep: 5,
		dataFormat: "%m/%d/%y",
		tooltipWidth: 90,
	},
	[SectorsTimeRange.FifteenDays]: {
		fields: priceResponseFieldsIntraday,
		rmrFields: rmrResponseFields,
		rmrClosePrice: "index_ratio",
		requestPath: "intraday/1h",
		isIntraday: true,
		duration: 15 * 24 * 60 * ONE_MIN_IN_MS,
		closePrice: "close",
		xAxisStep: 15,
		dataFormat: "%m/%d/%y %I:%M %p",
		tooltipWidth: 160,
	},
};

export type InitialStocks = Record<SectorsTimeRange, PriceDaily[]>;
export type InitialRmrData = Record<SectorsTimeRange, RMR[]>;

export const initialStocksPrepared: InitialStocks = {
	[SectorsTimeRange.OneYear]: [],
	[SectorsTimeRange.SixtyDays]: [],
	[SectorsTimeRange.FifteenDays]: [],
};

export const initialRmrPrepared: InitialRmrData = {
	[SectorsTimeRange.OneYear]: [],
	[SectorsTimeRange.SixtyDays]: [],
	[SectorsTimeRange.FifteenDays]: [],
};
